import { DRAG_CIRCLE_DIAMETER, CANVAS_WIDTH, CANVAS_HEIGHT, FLOOR_HEIGHT, WALL_THICKNESS, CYCLE_BACKGROUNDS, PLAYABLE_AREA } from './constants';
import defaultSettings from './defaultSettings';
import { Text, Container, Graphics, Sprite, Texture, Assets, TilingSprite, BlurFilter } from 'pixi.js';
import grass from './images/golfup-grass-2-web-optimized.webp';
import logoSpriteImg from './images/logo.png';
import { Application } from 'pixi.js';
import ViewportManager from './viewportManager';
import BallManager from './ballManager';

export default class RenderManager {
    private app: Application;
    private gameWorld: Container;
    private viewportManager: ViewportManager;
    private backgroundContainer: Container;
    private uiContainer: Container;
    private settings: any;
    private currentControlType: string;
    private backgroundImages: string[];
    private currentBackgroundIndex: number;
    private backgroundSprite: Sprite | null;
    private gradientGraphic: Graphics | null;
    private centerCircle: Graphics | null;
    private aimLine: Graphics;
    private throughLine: Graphics | null;
    private puttingLine: Graphics;
    private cursorTriangle: Graphics;
    public ballManager: BallManager | undefined;
    private floorSprite: TilingSprite | null;
    private greenHill: Graphics | null = null;

    private cursorTargetColor: number;
    private cursorCurrentColor: number;
    private colorTransitionSpeed: number;
    logoSpriteBG: Sprite;
    logoSprite: Sprite;

    private dragZone: Graphics | null

    private backgroundBlur: BlurFilter;

    private powerUpText: Text;
    private powerUpGraphics: Graphics;
    private cloudsContainer: Container;
    private cloudData: Array<{
        container: Container;
        initialX: number;
        initialY: number;
        speed: number; // pixels per second
        parallaxMultiplier: number; // how much the cloud moves relative to viewport scroll
    }> = [];
    private cloudAnimationTime: number = 0;

    constructor(app: Application, gameWorld: Container, viewportManager: ViewportManager) {

        this.app = app;
        this.gameWorld = gameWorld;
        this.viewportManager = viewportManager;
        this.backgroundContainer = new Container();
        this.uiContainer = new Container();
        this.app.stage.addChild(this.backgroundContainer);
        this.app.stage.addChild(this.gameWorld);
        this.app.stage.addChild(this.uiContainer);


        this.settings = JSON.parse(localStorage.getItem('golfUpSettings') || '{}') || defaultSettings;
        this.currentControlType = this.settings.player.controlType;


        this.backgroundImages = [
            "/images/backgrounds/beach-1.jpg",
            "/images/backgrounds/grass-1.jpg",
            "/images/backgrounds/grass-2.jpg",
            "/images/backgrounds/snow-1.jpg",
            "/images/backgrounds/smaller/bubble-1.webp",
            "/images/backgrounds/smaller/bubble-8.webp",
            "/images/backgrounds/smaller/bubble-9.webp",
            "/images/backgrounds/smaller/bubble-10.webp",
            "/images/backgrounds/smaller/aurora-over-snowy-mountains-northern-lights-2023-11-27-05-17-20-utc.webp",
            "/images/backgrounds/smaller/northern-lights-above-mountains-at-night-aurora-b-2023-11-27-04-58-44-utc.webp",
            "/images/backgrounds/smaller/shanghai-modern-building-skyline-in-nightfall-2023-11-27-05-26-26-utc.webp",
            "/images/backgrounds/smaller/output.webp",
            "/images/backgrounds/smaller/bridge-in-a-park-2023-11-27-05-12-04-utc.jpg",
            "/images/backgrounds/smaller/maldives-indian-ocean-2023-11-27-04-57-18-utc.jpg",
            "/images/backgrounds/smaller/martvili-canyon-in-georgia-nature-landscape-2023-11-27-05-08-45-utc.jpg",
            "/images/backgrounds/smaller/tall-oak-trees-with-young-spring-foliage-leaves-s-2023-11-27-04-55-13-utc.webp",
        ];

        this.currentBackgroundIndex = Math.min(parseInt(localStorage.getItem('backgroundIndex') || '0'), this.backgroundImages.length - 1);
        this.backgroundSprite = null;
        this.gradientGraphic = null;
        this.backgroundBlur = new BlurFilter({ strength: 2 });

        this.setupBackground();
        // this.setupGrassTexture();

        this.centerCircle = null;
        this.aimLine = new Graphics();
        this.uiContainer.addChild(this.aimLine);

        this.puttingLine = new Graphics();
        this.gameWorld.addChild(this.puttingLine);
        this.uiContainer.addChild(this.puttingLine);

        this.cursorTriangle = this.createCursorTriangle();

        this.setupControlScheme(this.currentControlType);
        this.ballManager = undefined;

        window.addEventListener('resize', this.handleResize.bind(this));

        // Logo removed - using new design system
        // this.createLogo();

        // Create green hill at bottom (where logo was)
        this.createGreenHill();

        this.cursorTargetColor = 0xAAFF33;  // Green color
        this.cursorCurrentColor = 0xAAFF33;
        this.colorTransitionSpeed = 0.1;  // Adjust this value to change transition speed

        this.powerUpText = new Text({ text: 'DOUBLE JUMP', style: { fontSize: 16, fill: 0xFFD700, fontFamily: 'Arial', fontWeight: 'bold', stroke: { color: 0x000000, width: 2 } } });
        this.powerUpText.anchor.set(0.5);
        this.powerUpText.visible = false;
        this.uiContainer.addChild(this.powerUpText);

        this.powerUpGraphics = new Graphics();
        this.uiContainer.addChild(this.powerUpGraphics);

        // Create clouds container
        this.cloudsContainer = new Container();
        this.backgroundContainer.addChild(this.cloudsContainer);
        this.createClouds();

    }

    private interpolateColor(color1: number, color2: number, factor: number): number {
        const r1 = (color1 >> 16) & 0xFF;
        const g1 = (color1 >> 8) & 0xFF;
        const b1 = color1 & 0xFF;

        const r2 = (color2 >> 16) & 0xFF;
        const g2 = (color2 >> 8) & 0xFF;
        const b2 = color2 & 0xFF;

        const r = Math.round(r1 + factor * (r2 - r1));
        const g = Math.round(g1 + factor * (g2 - g1));
        const b = Math.round(b1 + factor * (b2 - b1));

        return (r << 16) | (g << 8) | b;
    }

    setupControlScheme(controlType: string): void {
        this.currentControlType = controlType;
        if (controlType === "click-drag") {
            this.setupClickDragMode();
        } else {
            this.setupOneClickMode();
        }
    }

    setupClickDragMode(): void {

        this.puttingLine.visible = true;
    }

    setupOneClickMode(): void {
        this.clearDragZone();
        this.puttingLine.visible = false;
    }

    setupBackground(): void {
        if (CYCLE_BACKGROUNDS) {
            this.setupCyclingBackgrounds();
        } else {
            this.setupGradientBackground();
        }
    }

    setupCyclingBackgrounds(): void {
        this.backgroundSprite = new Sprite();
        this.backgroundSprite.filters = [this.backgroundBlur];
        this.backgroundContainer.addChild(this.backgroundSprite);
        this.loadNextBackground(false);
    }

    async loadNextBackground(inc: boolean = true): Promise<void> {
        try {
            if (inc) this.currentBackgroundIndex = (this.currentBackgroundIndex + 1) % this.backgroundImages.length;
            const texture = await Assets.load(this.backgroundImages[this.currentBackgroundIndex]);

            if (this.backgroundSprite) {
                this.backgroundSprite.texture = texture;
                this.recenterBackground();
            }

            localStorage.setItem('backgroundIndex', this.currentBackgroundIndex.toString());
        } catch (e) {
            console.log("Error loading background image", e);
            this.setupGradientBackground();
        }
    }

    setupGradientBackground(): void {
        if (this.gradientGraphic) {
            this.backgroundContainer.removeChild(this.gradientGraphic);
            this.gradientGraphic.destroy();
            this.gradientGraphic = null;
        }

        const viewportWidth = window.innerWidth || this.app.screen.width;
        const viewportHeight = window.innerHeight || this.app.screen.height;
        const background = new Graphics();
        const gradientHeight = viewportHeight;
        const topColor = 0xB8E6FF;
        const bottomColor = 0xFFFFFF;

        for (let i = 0; i < gradientHeight; i++) {
            const ratio = i / gradientHeight;
            const color = this.lerpColor(topColor, bottomColor, ratio);
            background.rect(0, i, viewportWidth, 1);
            background.fill({ color });
        }

        this.gradientGraphic = background;
        this.backgroundContainer.removeChildren();
        this.backgroundContainer.addChild(background);

        // Re-add clouds after background
        if (this.cloudsContainer) {
            this.backgroundContainer.addChild(this.cloudsContainer);
        }
    }

    /**
     * Creates cute simple clouds matching the design from new-main.html
     * Clouds are positioned in world space and will scroll with the viewport
     * Each cloud is drawn as a single Graphics object to prevent additive transparency blending
     */
    private createClouds(): void {
        if (!this.cloudsContainer) {
            this.cloudsContainer = new Container();
        }

        this.cloudsContainer.removeChildren();
        this.cloudData = [];

        const cloudColor = 0xE5F6FD;
        const createLayeredCloud = (
            mainWidth: number,
            mainHeight: number,
            overlay?: {
                size: number;
                offsetX: number;
                offsetY: number;
            }
        ): Container => {
            const cloud = new Container();

            const base = new Graphics();
            base.roundRect(0, 0, mainWidth, mainHeight, mainHeight / 2); // pill shape
            base.fill({ color: cloudColor, alpha: 1 });
            base.roundPixels = false;
            cloud.addChild(base);

            if (overlay) {
                const topper = new Graphics();
                topper.circle(
                    overlay.offsetX + overlay.size / 2,
                    overlay.offsetY + overlay.size / 2,
                    overlay.size / 2
                );
                topper.fill({ color: cloudColor, alpha: 1 });
                topper.roundPixels = false;
                cloud.addChild(topper);
            }

            return cloud;
        };

        // Cloud 1: top-20 left-20, opacity-60
        const cloud1 = createLayeredCloud(128, 64, {
            size: 80,
            offsetX: 24,
            offsetY: -32
        });
        const cloud1X = WALL_THICKNESS + 80; // left-20 = 80px from left wall
        const cloud1Y = 80; // top-20 = 80px from top
        cloud1.x = cloud1X;
        cloud1.y = cloud1Y;
        this.cloudsContainer.addChild(cloud1);
        this.cloudData.push({
            container: cloud1,
            initialX: cloud1X,
            initialY: cloud1Y,
            speed: 8, // pixels per second (slow drift)
            parallaxMultiplier: 0.15 // moves 15% of viewport scroll
        });

        // Cloud 2: top-40 right-40, opacity-40
        const cloud2 = createLayeredCloud(96, 48);
        const cloud2X = WALL_THICKNESS + PLAYABLE_AREA - 96 - 160; // right-40 = 160px from right edge
        const cloud2Y = 160; // top-40 = 160px
        cloud2.x = cloud2X;
        cloud2.y = cloud2Y;
        this.cloudsContainer.addChild(cloud2);
        this.cloudData.push({
            container: cloud2,
            initialX: cloud2X,
            initialY: cloud2Y,
            speed: -12, // negative = moves left
            parallaxMultiplier: 0.25 // moves 25% of viewport scroll
        });

        // Cloud 3: top-60 left-10, opacity-50
        const cloud3 = createLayeredCloud(112, 56, {
            size: 64,
            offsetX: 32,
            offsetY: -24
        });
        const cloud3X = WALL_THICKNESS + 40; // left-10 = 40px from left wall
        const cloud3Y = 240; // top-60 = 240px
        cloud3.x = cloud3X;
        cloud3.y = cloud3Y;
        this.cloudsContainer.addChild(cloud3);
        this.cloudData.push({
            container: cloud3,
            initialX: cloud3X,
            initialY: cloud3Y,
            speed: 10, // pixels per second
            parallaxMultiplier: 0.20 // moves 20% of viewport scroll
        });

        // Cloud 4: top-80 right-20, opacity-45
        const cloud4 = createLayeredCloud(80, 40);
        const cloud4X = WALL_THICKNESS + PLAYABLE_AREA - 80 - 80; // right-20 = 80px from right edge
        const cloud4Y = 320; // top-80 = 320px
        cloud4.x = cloud4X;
        cloud4.y = cloud4Y;
        this.cloudsContainer.addChild(cloud4);
        this.cloudData.push({
            container: cloud4,
            initialX: cloud4X,
            initialY: cloud4Y,
            speed: -6, // negative = moves left, slower
            parallaxMultiplier: 0.10 // moves 10% of viewport scroll (most subtle)
        });
    }

    lerpColor(a: number, b: number, amount: number): number {
        const ar = (a >> 16) & 0xff;
        const ag = (a >> 8) & 0xff;
        const ab = a & 0xff;
        const br = (b >> 16) & 0xff;
        const bg = (b >> 8) & 0xff;
        const bb = b & 0xff;
        const rr = ar + amount * (br - ar);
        const rg = ag + amount * (bg - ag);
        const rb = ab + amount * (bb - ab);
        return (rr << 16) + (rg << 8) + (rb | 0);
    }

    getColorForPower(power: number): number {
        // Power is expected to be 0-1
        const clampedPower = Math.max(0, Math.min(1, power));
        if (clampedPower < 0.5) {
            return this.lerpColor(0x00FF00, 0xFFFF00, clampedPower * 2); // Green to Yellow
        } else {
            return this.lerpColor(0xFFFF00, 0xFF0000, (clampedPower - 0.5) * 2); // Yellow to Red
        }
    }

    drawArrow(graphics: Graphics, start: { x: number, y: number }, end: { x: number, y: number }, color: number, alpha: number = 1, thickness: number = 2) {
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const headLength = 15;
        const headAngle = Math.PI / 6; // 30 degrees

        graphics.moveTo(start.x, start.y);
        graphics.lineTo(end.x, end.y);

        // Arrow head
        graphics.moveTo(end.x, end.y);
        graphics.lineTo(end.x - headLength * Math.cos(angle - headAngle), end.y - headLength * Math.sin(angle - headAngle));
        graphics.moveTo(end.x, end.y);
        graphics.lineTo(end.x - headLength * Math.cos(angle + headAngle), end.y - headLength * Math.sin(angle + headAngle));

        graphics.stroke({ width: thickness, color, alpha });
    }

    createCenterCircle(): void {
        if (!this.centerCircle) {
            this.centerCircle = new Graphics();
            this.uiContainer.addChild(this.centerCircle);
        }
        this.updateCenterCircle();
    }

    clearCenterCircle(): void {
        if (this.centerCircle) {
            this.uiContainer.removeChild(this.centerCircle);
            this.centerCircle = null;
        }
    }

    updateCenterCircle(): void {
        if (!this.centerCircle) return;

        const viewportWidth = window.innerWidth;
        const circleRadius = DRAG_CIRCLE_DIAMETER / 2;
        const lineWidth = 1;
        const centerX = viewportWidth / 2;
        const centerY = window.innerHeight * 0.75;

        this.centerCircle.clear();
        this.centerCircle.circle(centerX, centerY, circleRadius);
        this.centerCircle.stroke({ width: lineWidth, color: 0xFFFFFF });
        this.centerCircle.visible = true;
    }

    createDragZone(width: number, height: number, left: number, top: number) {
        if (!this.dragZone) {
            this.dragZone = new Graphics();
            this.uiContainer.addChild(this.dragZone);
        }
        this.updateDragZone(width, height, left, top);
    }

    updateDragZone(width: number, height: number, left: number, top: number) {
        this.dragZone?.clear();
        this.dragZone?.setStrokeStyle({ width: 2, color: 0xFFFFFF, alpha: 0.5 });
        this.dragZone?.rect(left, top, width, height);
        this.dragZone?.stroke();
    }

    clearDragZone() {
        if (this.dragZone) {
            this.uiContainer.removeChild(this.dragZone);
            this.dragZone = null;
        }
    }


    drawAimLine(startX: number, startY: number, endX: number, endY: number): void {
        this.aimLine.clear();
        this.aimLine.moveTo(startX, startY);
        this.aimLine.lineTo(endX, endY);
        this.aimLine.stroke({ width: 3, color: 0xFFFFFF });
    }

    clearAimLine(): void {
        this.aimLine.clear();
    }

    createCursorTriangle(): Graphics {
        const cursorTriangle = new Graphics();
        // set zIndex to lower than the force text
        cursorTriangle.zIndex = 100
        this.uiContainer.addChild(cursorTriangle);
        return cursorTriangle;
    }

    createThroughLine(): Graphics {
        this.throughLine = new Graphics();
        this.uiContainer.addChild(this.throughLine);
        return this.throughLine;
    }

    createPuttingLine(): Graphics {
        const puttingLine = new Graphics();
        this.gameWorld.addChild(puttingLine);
        return puttingLine;
    }

    createForceText(): Text {
        const forceText = new Text({ text: 'Force: 0', style: { fontFamily: 'Verdana', fontSize: 12, fill: 0xFFFFFF, stroke: 0xAAAAAA } });
        forceText.anchor.set(0);
        forceText.zIndex = 100000;
        forceText.visible = false;
        this.uiContainer.addChild(forceText);
        return forceText;
    }

    updateCursorTriangle(screenPosition: { x: number, y: number }, targetPosition: { x: number, y: number }, force: number, cursorBounce: number = 0, isClickDragMode: boolean = false): void {
        this.cursorTriangle.clear();
        this.cursorTriangle.zIndex = 100000;

        const dx = targetPosition.x - screenPosition.x;
        const dy = targetPosition.y - screenPosition.y;
        const angle = Math.atan2(dy, dx);

        const bounceDistance = 2 * Math.sin(cursorBounce);
        const bouncedX = screenPosition.x + Math.cos(angle) * bounceDistance;
        const bouncedY = screenPosition.y + Math.sin(angle) * bounceDistance;

        const triangleSize = 20 + force * 300;
        const x1 = bouncedX + Math.cos(angle) * triangleSize;
        const y1 = bouncedY + Math.sin(angle) * triangleSize;
        const x2 = bouncedX + Math.cos(angle + 2.5) * triangleSize;
        const y2 = bouncedY + Math.sin(angle + 2.5) * triangleSize;
        const x3 = bouncedX + Math.cos(angle - 2.5) * triangleSize;
        const y3 = bouncedY + Math.sin(angle - 2.5) * triangleSize;

        const normalizedForce = (force - 0.002) / (0.04 - 0.002);

        let alpha = 0.5 + normalizedForce / 2;
        if (isClickDragMode) {
            const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);
            const maxDistance = DRAG_CIRCLE_DIAMETER / 2;
            alpha *= Math.min(distanceFromCenter / maxDistance, 1);
        }

        const color = this.cursorCurrentColor;
        this.cursorTriangle.poly([x1, y1, x2, y2, x3, y3]);
        this.cursorTriangle.fill({ color, alpha });


        this.cursorTriangle.visible = true;
    }

    clearCursorGraphics(): void {
        if (this.cursorTriangle) {
            this.cursorTriangle.visible = false;
        }
    }

    updateForceText(forceText: Text, force: number, x: number, y: number): void {
        forceText.text = `${force.toFixed(0)}`;
        forceText.x = x - 20;
        forceText.y = y + 20;
        forceText.visible = true;
    }

    updateThroughLine(worldPosition: { x: number, y: number }, ballPosition: { x: number, y: number }, magnitude: number): void {
        const throughLine = this.throughLine || this.createThroughLine();
        throughLine.clear();
        // if ball is in motion, don't draw the through line
        if (this.ballManager?.inMotion) {
            throughLine.visible = false;
            return;
        }
        throughLine.visible = true;

        const startScreenX = this.viewportManager.getScreenXFromWorldX(worldPosition.x - WALL_THICKNESS);
        const startScreenY = this.viewportManager.getScreenYFromWorldY(worldPosition.y);
        const endScreenX = this.viewportManager.getScreenXFromWorldX(ballPosition.x);
        const endScreenY = this.viewportManager.getScreenYFromWorldY(ballPosition.y);

        // Clamp the start and end points to the visible area (basic clamping, might need intersection logic for perfect edge cases)
        // For now, we just draw from ball to cursor.

        // Calculate power from magnitude (1-10)
        const normalizedPower = (magnitude - 1) / 9;
        const color = this.getColorForPower(normalizedPower);

        // Draw arrow from Ball to Cursor
        this.drawArrow(
            throughLine,
            { x: endScreenX, y: endScreenY },
            { x: startScreenX, y: startScreenY },
            color,
            0.8,
            3 + normalizedPower * 2 // Thicker line for more power
        );
    }

    update(delta: number): void {

        // Update cursor color
        const targetColor = this.ballManager?.inMotion ? 0xEEEEEE : 0xAAFF33;
        if (this.cursorTargetColor !== targetColor) {
            this.cursorTargetColor = targetColor;
        }

        this.cursorCurrentColor = this.interpolateColor(
            this.cursorCurrentColor,
            this.cursorTargetColor,
            this.colorTransitionSpeed
        );

        // Update Power Up UI
        if (this.ballManager && this.ballManager.gameModeHandler.hasDoubleJump()) {
            const progress = this.ballManager.getPowerUpCooldownProgress();
            const width = 200;
            const height = 20;
            const x = CANVAS_WIDTH / 2 - width / 2;
            const y = 80; // Top of screen below title/score

            this.powerUpGraphics.clear();

            if (progress < 1) {
                // Draw background
                this.powerUpGraphics.rect(x, y, width, height);
                this.powerUpGraphics.fill({ color: 0x000000, alpha: 0.5 });

                // Draw progress
                this.powerUpGraphics.rect(x, y, width * progress, height);
                this.powerUpGraphics.fill({ color: 0xFFFF00, alpha: 0.7 });

                this.powerUpText.visible = false;
            } else {
                // Ready state
                const pulse = Math.sin(Date.now() / 200) * 0.1 + 1;
                this.powerUpText.visible = true;
                this.powerUpText.position.set(CANVAS_WIDTH / 2, y + 10);
                this.powerUpText.scale.set(pulse);
            }
        } else {
            // Hide if ballManager doesn't exist or double jump is disabled
            this.powerUpGraphics.clear();
            this.powerUpText.visible = false;
        }

        // Update cloud animations
        this.updateClouds(delta);
    }

    private updateClouds(delta: number): void {
        if (!this.cloudsContainer || this.cloudData.length === 0) return;

        // Update animation time (in seconds)
        this.cloudAnimationTime += delta / 1000;

        // Get viewport scroll position for parallax effect
        const scrollPosition = this.viewportManager.scrollPosition;

        // Update each cloud's position
        this.cloudData.forEach((cloudInfo) => {
            // Calculate horizontal drift from initial position
            const driftDistance = this.cloudAnimationTime * cloudInfo.speed;
            
            // Calculate base X position
            let baseX = cloudInfo.initialX + driftDistance;
            
            // Wrap around if cloud goes off screen
            const leftBound = WALL_THICKNESS - 200;
            const rightBound = WALL_THICKNESS + PLAYABLE_AREA + 200;
            const range = rightBound - leftBound;
            
            // Normalize position to wrap around (works for both positive and negative)
            while (baseX > rightBound) {
                baseX -= range;
            }
            while (baseX < leftBound) {
                baseX += range;
            }

            // Apply parallax effect based on viewport scroll
            // Parallax moves clouds slightly in the opposite direction of scroll
            // This creates depth - clouds further "back" move less
            const parallaxOffset = scrollPosition * cloudInfo.parallaxMultiplier;

            // Set final position
            cloudInfo.container.x = baseX - parallaxOffset;
            cloudInfo.container.y = cloudInfo.initialY;
        });
    }

    calculateCoverDimensions(imageWidth: number, imageHeight: number, containerWidth: number, containerHeight: number): { width: number, height: number, x: number, y: number } {
        const imageRatio = imageWidth / imageHeight;
        const containerRatio = containerWidth / containerHeight;

        let newWidth, newHeight;

        if (containerRatio > imageRatio) {
            newWidth = containerWidth;
            newHeight = containerWidth / imageRatio;
        } else {
            newWidth = containerHeight * imageRatio;
            newHeight = containerHeight;
        }

        return {
            width: newWidth,
            height: newHeight,
            x: (containerWidth - newWidth) / 2,
            y: (containerHeight - newHeight) / 2
        };
    }

    updatePuttingLine(start: { x: number, y: number }, end: { x: number, y: number }): void {
        this.puttingLine.clear();

        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Estimate power based on drag distance (assuming similar scaling to cursor logic)
        // In InputManager: maxDragDistance is roughly dragZoneWidth/Height.
        // Let's just use a reasonable max pixel distance for visualization, e.g., 300px
        const maxDrag = 300;
        const normalizedPower = Math.min(distance / maxDrag, 1);
        const color = this.getColorForPower(normalizedPower);

        this.drawArrow(
            this.puttingLine,
            start,
            end,
            color,
            0.8,
            3 + normalizedPower * 2
        );
    }

    clearPuttingLine(): void {
        this.puttingLine.clear();
    }

    recenterBackground(): void {
        if (this.backgroundSprite) {
            const texture = this.backgroundSprite.texture;
            const imageAspectRatio = texture.width / texture.height;
            const canvasAspectRatio = CANVAS_WIDTH / CANVAS_HEIGHT;

            let newWidth, newHeight;

            if (canvasAspectRatio > imageAspectRatio) {
                // Canvas is wider than the image
                newWidth = CANVAS_WIDTH;
                newHeight = CANVAS_WIDTH / imageAspectRatio;
            } else {
                // Canvas is taller than the image
                newHeight = CANVAS_HEIGHT;
                newWidth = CANVAS_HEIGHT * imageAspectRatio;
            }

            this.backgroundSprite.width = newWidth;
            this.backgroundSprite.height = newHeight;

            // Center horizontally
            this.backgroundSprite.x = (CANVAS_WIDTH - newWidth) / 2;

            // Align bottom with canvas bottom
            this.backgroundSprite.y = CANVAS_HEIGHT - newHeight;
        }
    }

    handleResize(): void {
        if (CYCLE_BACKGROUNDS) {
            this.recenterBackground();
        } else {
            this.setupGradientBackground();
        }
        // Recreate clouds on resize to update positions
        this.createClouds();
        // Logo removed - using new design system
        // this.updateLogo();
    }
    async setupGrassTexture(): Promise<void> {
        const texture = await Assets.load(grass);
        this.floorSprite = new TilingSprite({
            texture,
            width: CANVAS_WIDTH,
            height: FLOOR_HEIGHT,
        });
        this.floorSprite.position.set(0, CANVAS_HEIGHT - FLOOR_HEIGHT);
        this.gameWorld.addChild(this.floorSprite);
    }

    updateGrassTexture(): void {
        if (this.floorSprite) {
            this.floorSprite.width = window.innerWidth;
            this.floorSprite.height = FLOOR_HEIGHT;
            this.floorSprite.anchor.set(0.5, 0.5);
            this.floorSprite.position.set(this.floorSprite.width / 2, CANVAS_HEIGHT - FLOOR_HEIGHT);
        }
    }

    // Method to toggle control scheme
    toggleControlScheme(): void {
        this.currentControlType = this.currentControlType === "click-drag" ? "1-click" : "click-drag";
        this.setupControlScheme(this.currentControlType);
    }

    // Method to get current control type
    getCurrentControlType(): string {
        return this.currentControlType;
    }

    // Method to set ball manager
    setBallManager(ballManager: BallManager): void {
        this.ballManager = ballManager;
    }

    // Method to get the UI container
    getUIContainer(): Container {
        return this.uiContainer;
    }

    // Method to get the game world container
    getGameWorld(): Container {
        return this.gameWorld;
    }

    // Method to add a child to the game world
    addToGameWorld(child: PIXI.DisplayObject): void {
        this.gameWorld.addChild(child);
    }

    // Method to remove a child from the game world
    removeFromGameWorld(child: PIXI.DisplayObject): void {
        this.gameWorld.removeChild(child);
    }

    // Method to clear all graphics
    clearAllGraphics(): void {
        this.clearAimLine();
        this.clearPuttingLine();
        this.clearCursorGraphics();
    }

    // Method to show/hide UI elements
    setUIVisibility(visible: boolean): void {
        this.uiContainer.visible = visible;
    }

    // Method to get the current background index
    getCurrentBackgroundIndex(): number {
        return this.currentBackgroundIndex;
    }

    // Method to manually set the background
    setBackground(index: number): void {
        if (index >= 0 && index < this.backgroundImages.length) {
            this.currentBackgroundIndex = index;
            this.loadNextBackground(false);
        }
    }

    // Method to toggle background cycling
    toggleBackgroundCycling(enabled: boolean): void {
        // This method would need to be implemented if you want to allow toggling of background cycling at runtime
    }

    // Method to update game world position based on viewport
    updateGameWorldPosition(): void {
        const scrollY = this.viewportManager.scrollPosition;
        this.gameWorld.y = -scrollY * this.viewportManager.scale;
    }

    // Logo removed - using new design system
    // async createLogo(): Promise<void> {
    //     const texture = await Assets.load(logoSpriteImg)
    //     this.logoSpriteBG = new Sprite(texture);
    //     this.logoSpriteBG.scale.set(0.6);
    //     this.logoSpriteBG.anchor.set(0.5);
    //     this.logoSpriteBG.position.set(CANVAS_WIDTH / 2, CANVAS_HEIGHT - FLOOR_HEIGHT / 2);
    //     // make sure the logo is always on top
    //     this.logoSpriteBG.zIndex = 100
    //     this.gameWorld.addChild(this.logoSpriteBG);
    //
    //     this.signalReady();
    // }

    signalReady(): void {
        console.log('[LOADING] RenderManager.signalReady() called');
        // RenderManager is ready immediately since it doesn't have async loading
        if (this.signalReadyCallback) {
            this.signalReadyCallback();
        }
    }

    private signalReadyCallback: (() => void) | null = null;

    public set signalReady(callback: () => void) {
        this.signalReadyCallback = callback;
        console.log('[LOADING] RenderManager signalReady callback set');
        // Call it immediately since renderManager is ready
        callback();
    }

    /**
     * Create the green hill at the bottom (replacing the logo)
     * Matches the original CSS: border-radius: 120px 120px 0 0, height: 90px
     * Centered under the playable area
     */
    private createGreenHill(): void {
        // Remove old green hill if it exists
        if (this.greenHill && this.greenHill.parent) {
            this.gameWorld.removeChild(this.greenHill);
            this.greenHill.destroy();
        }

        const hill = new Graphics();

        // Green hill color: #82c9a5
        const hillColor = 0x82c9a5;
        const hillHeight = 290; // 90px height like original
        const hillY = CANVAS_HEIGHT - FLOOR_HEIGHT / 2 - hillHeight / 2; // Position where logo was
        const hillWidth = PLAYABLE_AREA;
        const cornerRadius = 120; // 120px radius like original CSS

        // Draw rounded rectangle with only top corners rounded (like CSS border-radius: 120px 120px 0 0)
        // Build path manually to match CSS exactly
        const segments = 32; // Smooth curve

        // Start at bottom-left (sharp corner)
        hill.moveTo(0, hillY + hillHeight);

        // Left edge (straight vertical line up to where the curve starts)
        hill.lineTo(0, hillY + cornerRadius);

        // Left top corner (rounded) - quarter circle arc
        // Arc center is at (cornerRadius, hillY + cornerRadius)
        // Arc goes from angle π (pointing left) to π/2 (pointing up)
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const angle = Math.PI + (t * Math.PI / 2); // From π (left) to π/2 (up)
            const x = cornerRadius + cornerRadius * Math.cos(angle);
            const y = hillY + cornerRadius + cornerRadius * Math.sin(angle);
            hill.lineTo(x, y);
        }

        // Top edge (straight horizontal line)
        hill.lineTo(hillWidth - cornerRadius, hillY);

        // Right top corner (rounded) - quarter circle arc
        // Arc center is at (hillWidth - cornerRadius, hillY + cornerRadius)
        // Arc goes from angle -π/2 (pointing up) to 0 (pointing right)
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const angle = -Math.PI / 2 + (t * Math.PI / 2); // From -π/2 (up) to 0 (right)
            const x = (hillWidth - cornerRadius) + cornerRadius * Math.cos(angle);
            const y = hillY + cornerRadius + cornerRadius * Math.sin(angle);
            hill.lineTo(x, y);
        }

        // Right edge and bottom edge (straight lines, sharp corners)
        hill.lineTo(hillWidth, hillY + hillHeight);
        hill.lineTo(0, hillY + hillHeight);
        hill.closePath();
        hill.fill({ color: hillColor });
        hill.stroke({ width: 4, color: 0x000000 }); // border-top: 4px solid #000
        hill.roundPixels = false; // Smooth rendering

        // Position the hill to be centered under the playable area
        hill.x = WALL_THICKNESS;
        hill.zIndex = 0;

        this.greenHill = hill;
        this.gameWorld.addChild(hill);
    }

    /**
     * Recreate green hill after canvas height changes (e.g., snakeup mode doubles height)
     * This should be called after game mode initialization if canvas height changed
     */
    public updateGreenHill(): void {
        console.log('[RenderManager] updateGreenHill called, CANVAS_HEIGHT:', CANVAS_HEIGHT);
        this.createGreenHill();
    }

    // Logo removed - using new design system
    // updateLogo(): void {
    //     this.logoSpriteBG = this.uiContainer.children.find((child) => child instanceof Sprite) as Sprite;
    //     if (this.logoSpriteBG) {
    //         this.logoSpriteBG.position.set(CANVAS_WIDTH / 2, CANVAS_HEIGHT - FLOOR_HEIGHT / 2);
    //     }
    // }

    clearAimingGraphics() {
        this.cursorTriangle.clear();
        this.throughLine?.clear();
    }
}